import { EConnectionEvents, EWebsocketClientEvents, TAllEvents, TWebSocketMessage } from 'webrtc/events';
import { ISignalClient, TListener, TRawConnection, TSignalCallback } from './types';

export class SignalerClient implements ISignalClient {
  public wsApiUrl: string;

  private wsClient!: WebSocket;

  private listeners: TListener[];

  constructor(wsApiUrl: string) {
    this.wsApiUrl = wsApiUrl;
    this.listeners = [];
  }

  public signal(message: TWebSocketMessage) {
    if (!this.wsClient) {
      return;
    }

    if (this.wsClient.readyState === WebSocket.OPEN) {
      this.wsClient.send(JSON.stringify(message));
    } else if (this.wsClient.readyState === WebSocket.CONNECTING) {
      this.wsClient.onopen = () => {
        this.wsClient.send(JSON.stringify(message));
      };
    } else {
      // ignore signals sent while closing or closed
    }
  }

  public async connect(): Promise<TRawConnection> {
    this.disconnect();
    return new Promise((res, rej) => {
      this.wsClient = new WebSocket(this.wsApiUrl);
      this.setRawListeners();

      this.on(EWebsocketClientEvents.WsClientOpen, () => {
        this.signal({ action: EConnectionEvents.HandshakeStart, data: {} });
      });

      this.on(EConnectionEvents.HandshakeComplete, (connection: TRawConnection) => {
        this.off(EWebsocketClientEvents.WsClientError);
        res(connection);
      });

      this.on(EWebsocketClientEvents.WsClientError, (e) => {
        rej(e);
      });
    });
  }

  public on(event: TAllEvents, callback: TSignalCallback) {
    this.listeners.push({ event, callback });
  }

  public off(event: TAllEvents) {
    this.listeners = this.listeners.filter(({ event: e }) => e !== event);
  }

  public disconnect() {
    if (!this.wsClient) {
      return;
    }
    if (this.isReady()) {
      this.wsClient.close();
      this.listeners = [];
    }
  }

  private setRawListeners = () => {
    this.wsClient.onopen = () => {
      this.callListener(EWebsocketClientEvents.WsClientOpen);
    };

    this.wsClient.onclose = () => this.callListener(EWebsocketClientEvents.WsClientClosed);

    this.wsClient.onerror = () => this.callListener(EWebsocketClientEvents.WsClientError);

    this.wsClient.onmessage = (event) => {
      // console.log('New message Event: ', event);
      const { action, from, data } = JSON.parse(event.data);
      this.callListener(action, data, from);
    };
  };

  private callListener(event: TAllEvents, data?: Record<string, unknown>, from?: string) {
    this.listeners.forEach((listener) => {
      if (listener.event === event) listener.callback(data, from);
    });
  }

  public isReady() {
    return this.wsClient.readyState === WebSocket.CONNECTING || this.wsClient.readyState === WebSocket.OPEN;
  }
}
